import { AxiosError } from 'axios';
import { ServerResponse, WithAuditDetails } from 'data/types';
import { History } from 'history';
import { Dispatch, Action } from 'redux';
import { RootState } from 'data/root.reducers';
import i18n from 'i18n';
import { showSuccessMessage, showError } from 'shared/toaster';
import { ThunkDispatch } from 'redux-thunk';
import * as certStatusApi from 'data/certStatus/certStatus.api';
import { CertStatus, CREATE_CERT_STATUS } from 'data/certStatus/certStatus.types';
import { extractErrorMessage } from '../../utils';
import * as api from './tlsProfile.api';
import { getSelectedTlsProfile } from './tlsProfile.selectors';
import {
  GET_TLS_PROFILE_LIST,
  GET_TLS_PROFILE,
  CREATE_TLS_PROFILE,
  UPDATE_TLS_PROFILE,
  DELETE_TLS_PROFILE,
  GO_TO_TLS_PROFILE_LIST,
  GO_TO_TLS_PROFILE_EDIT,
  GO_TO_TLS_PROFILE_VIEW,
  GO_TO_TLS_PROFILE_CREATE,
  SELECT_TLS_PROFILE,
  DeleteTlsProfileThunkAction,
  UpdateTlsProfileThunkAction,
  CreateTlsProfileThunkAction,
  SET_TLS_PROFILE_LOADING,
  SetTlsProfileLoadingAction,
  TlsProfileActionTypes,
  TlsProfile,
  SelectTlsProfileAction,
} from './tlsProfile.types';
import { TLS_PROFILE, SYSTEM_CONFIGURATION_ROUTE, SECURITY } from '../../constants';

export function goToTlsProfileList(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_TLS_PROFILE_LIST,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${SECURITY}${TLS_PROFILE}`);
  };
}

export function goToTlsProfileCreate(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_TLS_PROFILE_CREATE,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${SECURITY}${TLS_PROFILE}/create`);
  };
}

export function goToTlsProfileEdit(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_TLS_PROFILE_EDIT,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${SECURITY}${TLS_PROFILE}/edit`);
  };
}

export function goToTlsProfileView(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_TLS_PROFILE_VIEW,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${SECURITY}${TLS_PROFILE}/view`);
  };
}

export function setTlsProfileLoadingAction(
  isLoading: boolean,
): SetTlsProfileLoadingAction {
  return {
    type: SET_TLS_PROFILE_LOADING,
    payload: { isLoading },
  };
}

export function getTlsProfileList(deliveryFunctionId: string): TlsProfileActionTypes {
  return {
    type: GET_TLS_PROFILE_LIST,
    payload: api.getTlsProfileList(deliveryFunctionId),
  };
}

export function getTlsProfile(
  deliveryFunctionId: string, id: string,
): TlsProfileActionTypes {
  return {
    type: GET_TLS_PROFILE,
    payload: api.getTlsProfile(deliveryFunctionId, id),
  };
}

export function createTlsProfile(
  deliveryFunctionId: string,
  body: WithAuditDetails<TlsProfile>,
  history: History,
  certStatusBody?: WithAuditDetails<CertStatus>,
): CreateTlsProfileThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    try {
      await dispatch({
        type: CREATE_TLS_PROFILE,
        payload: api.createTlsProfile(deliveryFunctionId, body),
      });

      if (certStatusBody) {
        await dispatch({
          type: CREATE_CERT_STATUS,
          payload: certStatusApi.createCertStatus(deliveryFunctionId, certStatusBody),
        });
      }

      dispatch(getTlsProfileList(deliveryFunctionId));
      dispatch(goToTlsProfileList(history));
      showSuccessMessage(i18n.t('tlsProfile.create.success', { name: body.data?.name }), 5000);

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('tlsProfile.create.failed', { name: body.data?.name, error: errorMessage }));
    }
  };
}

export function createTlsProfileFromForm(
  deliveryFunctionId: string,
  body: WithAuditDetails<TlsProfile>,
): CreateTlsProfileThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    dispatch(setTlsProfileLoadingAction(true));
    try {
      await dispatch({
        type: CREATE_TLS_PROFILE,
        payload: api.createTlsProfile(deliveryFunctionId, body),
      });

      showSuccessMessage(i18n.t('tlsProfile.create.success', { name: body.data?.name }), 5000);

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('tlsProfile.create.failed', { name: body.data?.name, error: errorMessage }));

    } finally {
      dispatch(setTlsProfileLoadingAction(false));
      dispatch(getTlsProfileList(deliveryFunctionId));
    }
  };
}

export function updateTlsProfile(
  deliveryFunctionId: string,
  id: string,
  body: WithAuditDetails<TlsProfile>,
  history: History,
): UpdateTlsProfileThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    try {
      await dispatch({
        type: UPDATE_TLS_PROFILE,
        payload: api.updateTlsProfile(deliveryFunctionId, id, body),
      });

      dispatch(getTlsProfileList(deliveryFunctionId));
      dispatch(goToTlsProfileList(history));
      showSuccessMessage(i18n.t('tlsProfile.update.success', { name: body.data?.name }), 5000);

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('tlsProfile.update.failed', { name: body.data?.name, error: errorMessage }));
    }
  };
}

export function deleteTlsProfile(
  deliveryFunctionId: string,
  id: string,
  body: WithAuditDetails<void>,
  history?: History,
): DeleteTlsProfileThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>, getState: () => RootState) => {
    const [selectedTlsProfile] = getSelectedTlsProfile(getState());

    try {
      await dispatch({
        type: DELETE_TLS_PROFILE,
        payload: api.deleteTlsProfile(deliveryFunctionId, id, body),
      });

      showSuccessMessage(i18n.t('tlsProfile.delete.success', { name: selectedTlsProfile?.name }), 5000);
      if (history != null) {
        dispatch(goToTlsProfileList(history));
      }
    } catch (e) {
      showError(i18n.t('tlsProfile.delete.failed', { name: selectedTlsProfile?.name }));

    } finally {
      dispatch(getTlsProfileList(deliveryFunctionId));
    }
  };
}

export function selectTlsProfile(
  tlsProfile: TlsProfile[],
): SelectTlsProfileAction {
  return {
    type: SELECT_TLS_PROFILE,
    payload: tlsProfile,
  };
}
